import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import type {
  TArticleAttachmentDto,
  TArticleCreatePayload,
  TArticleEduCode,
  TArticleModelDto,
  TArticleUpdatePayload,
} from '~/api/oneBoxV3/oneBoxV3.type';
import type {
  TLatestNewsAttachment,
  TLatestNewsItem,
} from './latestNewsStorage';

const ONEBOX3_API_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_ONEBOX3_API_DOMAIN
    ? String(import.meta.env.VITE_ONEBOX3_API_DOMAIN).replace(/\/$/, '')
    : '';

/**
 * 將後端可能傳入「非字串」（null／undefined／陣列／物件／數字…）的值統一轉成字串，
 * 避免 `subject.trim()`、`coverImageUrl.trim()` 等在編輯器與驗證流程中直接擲錯。
 */
const safeStr = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  return String(v);
};

/** 後端若回傳相對路徑，補上 API 網域以便 `<img>`／新視窗附件可載入 */
export const resolveOneBoxV3MediaUrl = (u?: unknown): string => {
  const t = safeStr(u).trim();
  if (!t) return '';
  if (t.startsWith('data:') || /^https?:\/\//i.test(t)) return t;
  if (!ONEBOX3_API_BASE) return t;
  return `${ONEBOX3_API_BASE}${t.startsWith('/') ? '' : '/'}${t}`;
};

export const ARTICLE_LIST_PAGE_SIZE = 100;

export const defaultArticlePagination = () => ({
  page: 1 as number | null,
  pageSize: ARTICLE_LIST_PAGE_SIZE as number | null,
  sort: '-publishAt' as string | null,
  paginateBy: 'publishAt' as string | null,
});

const eduToApi = (edu: resourceEduCode): TArticleEduCode =>
  edu as TArticleEduCode;

const eduFromApi = (edu: string): resourceEduCode => {
  if (edu === '國中') return resourceEduCode.J;
  if (edu === '高中') return resourceEduCode.H;
  return resourceEduCode.E;
};

/**
 * 後端 Article 模型欄位為單一字串時，多選常以「國小,國中」或「114上、113下」回傳。
 * 若不拆分，會被當成單一選項，導致 MUI Select 無法對應 MenuItem。
 */
const splitLegacyMultiValue = (raw: string): string[] => {
  const t = raw.trim();
  if (!t) return [];
  const parts = t
    .split(/[,、，]/)
    .map(s => s.trim())
    .filter(Boolean);
  return parts.length ? parts : [t];
};

/** 後端若仍回傳單一字串，或將來改為陣列，皆轉成去重後的字串陣列 */
const strArrayFromDto = (v: unknown, legacy: unknown): string[] => {
  if (Array.isArray(v)) {
    const out = v
      .filter((x): x is string => typeof x === 'string')
      .flatMap(s => splitLegacyMultiValue(s))
      .filter(Boolean);
    return [...new Set(out)];
  }
  const s = safeStr(v).trim();
  if (s) return [...new Set(splitLegacyMultiValue(s))];
  const leg = safeStr(legacy).trim();
  return leg ? [...new Set(splitLegacyMultiValue(leg))] : [];
};

const eduCodesFromDto = (v: unknown, legacy: unknown): resourceEduCode[] => {
  if (Array.isArray(v)) {
    const out = v
      .filter((x): x is string => typeof x === 'string')
      .flatMap(x => splitLegacyMultiValue(x))
      .map(x => eduFromApi(x));
    return [...new Set(out)];
  }
  const s = safeStr(v).trim();
  if (s) {
    return [...new Set(splitLegacyMultiValue(s).map(x => eduFromApi(x)))];
  }
  const leg = safeStr(legacy).trim();
  return leg
    ? [...new Set(splitLegacyMultiValue(leg).map(x => eduFromApi(x)))]
    : [];
};

const tsFromIso = (s?: string) => {
  if (!s) return Date.now();
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : Date.now();
};

export const attachmentsToApi = (
  list: TLatestNewsAttachment[],
): TArticleAttachmentDto[] =>
  [...list]
    .sort((a, b) => a.sort - b.sort)
    .map((a, i) => ({
      url: safeStr(a.url).trim(),
      fileName: safeStr(a.fileName).trim() || '附件',
      fileSize: a.sizeBytes || 0,
      order: typeof a.sort === 'number' ? a.sort : i,
    }))
    .filter(a => a.url.length > 0);

export const articleDtoToLatestNewsItem = (
  dto: TArticleModelDto,
): TLatestNewsItem => {
  const kw = Array.isArray(dto.keywords)
    ? dto.keywords.filter((k): k is string => typeof k === 'string')
    : [];
  const rawAttachments = Array.isArray(dto.attachments) ? dto.attachments : [];
  const attachments: TLatestNewsAttachment[] = rawAttachments.map((x, idx) => ({
    id: `${safeStr(dto._id)}-att-${x?.order ?? idx}-${idx}`,
    fileName: safeStr(x?.fileName),
    url: resolveOneBoxV3MediaUrl(x?.url),
    sizeBytes: typeof x?.fileSize === 'number' ? x.fileSize : 0,
    mime: 'application/octet-stream',
    sort: typeof x?.order === 'number' ? x.order : idx,
  }));
  const publishAtStr = safeStr(dto.publishAt);
  const publishedDate =
    publishAtStr.length >= 10
      ? publishAtStr.slice(0, 10)
      : new Date().toISOString().slice(0, 10);

  const title = safeStr(dto.title);
  const summary = safeStr(dto.summary);
  const content = safeStr(dto.content) || '<p></p>';

  const dtoAny = dto as TArticleModelDto & {
    eduCodes?: unknown;
    subjects?: unknown;
    academicYears?: unknown;
    grades?: unknown;
  };

  return {
    id: safeStr(dto._id),
    title,
    summary,
    contentHtml: content,
    tags: [...kw],
    publishedAt: publishedDate,
    publishAt: publishAtStr || undefined,
    takeDownAt: null,
    eduCodes: eduCodesFromDto(dtoAny.eduCodes, dto.eduCode),
    subjects: strArrayFromDto(dtoAny.subjects, dto.subject),
    schoolYears: strArrayFromDto(dtoAny.academicYears, dto.academicYear),
    grades: strArrayFromDto(dtoAny.grades, dto.grade),
    coverImageUrl: resolveOneBoxV3MediaUrl(dto.coverImageUrl),
    coverMediaId: '',
    mediaType: null,
    mediaDataUrl: null,
    youtubeUrl: safeStr(dto.youtubeUrl),
    attachments,
    seoMetaTitle: title,
    seoMetaDescription: summary,
    seoOgImageUrl: resolveOneBoxV3MediaUrl(dto.coverImageUrl),
    status: dto.status === 'published' ? 'published' : 'draft',
    sortAssist: null,
    createdAt: tsFromIso(safeStr(dto.createdAt)),
    updatedAt: tsFromIso(safeStr(dto.updatedAt)),
    createdBy: safeStr(dto.createdBy),
    updatedBy: '',
    disabled: Boolean(dto.disabled),
  };
};

/**
 * 後台編輯開啟時做快照，避免與列表列物件共用參考（例如 tags／附件陣列），
 * 並清除僅屬於表單的 pending 狀態。
 */
export const snapshotLatestNewsItemForEdit = (
  row: TLatestNewsItem,
): TLatestNewsItem => ({
  ...row,
  tags: [...row.tags],
  eduCodes: [...row.eduCodes],
  subjects: [...row.subjects],
  schoolYears: [...row.schoolYears],
  grades: [...row.grades],
  attachments: row.attachments.map(a => ({
    ...a,
    pendingFile: undefined,
  })),
});

/**
 * 表單「上架日期」為 {@link TLatestNewsItem.publishedAt}（YYYY-MM-DD）；
 * {@link TLatestNewsItem.publishAt} 為後端完整字串。若一律優先用 publishAt，
 * 使用者只改日期仍會送出舊的 publishAt（例如仍為五月）。
 */
const publishAtStringForApi = (row: TLatestNewsItem): string => {
  const rawDay = row.publishedAt.trim();
  const day = rawDay.length >= 10 ? rawDay.slice(0, 10) : '';
  const fromApi = row.publishAt?.trim();

  if (day.length === 10) {
    if (fromApi) {
      const apiDay = fromApi.slice(0, 10);
      if (apiDay === day) {
        return fromApi;
      }
    }
    return `${day}T00:00:00.000Z`;
  }

  if (fromApi) return fromApi;
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
};

export const latestNewsToCreatePayload = (
  row: TLatestNewsItem,
  coverImageUrl: string,
  attachments: TArticleAttachmentDto[],
): TArticleCreatePayload => ({
  eduCode: [...new Set(row.eduCodes.map(eduToApi))],
  subject: [...new Set(row.subjects.map(s => s.trim()).filter(Boolean))],
  academicYear: [
    ...new Set(row.schoolYears.map(s => s.trim()).filter(Boolean)),
  ],
  grade: [...new Set(row.grades.map(s => s.trim()).filter(Boolean))],
  title: row.title.trim(),
  publishAt: publishAtStringForApi(row),
  keywords: row.tags.length ? row.tags : undefined,
  summary: row.summary.trim() || undefined,
  content: row.contentHtml || '<p></p>',
  coverImageUrl: coverImageUrl.trim() || undefined,
  youtubeUrl: row.youtubeUrl.trim() || undefined,
  attachments: attachments.length ? attachments : undefined,
  status: row.status,
});

export const latestNewsToUpdatePayload = (
  row: TLatestNewsItem,
  coverImageUrl: string,
  attachments: TArticleAttachmentDto[],
): TArticleUpdatePayload => ({
  id: row.id,
  eduCode: [...new Set(row.eduCodes.map(eduToApi))],
  subject: [...new Set(row.subjects.map(s => s.trim()).filter(Boolean))],
  academicYear: [
    ...new Set(row.schoolYears.map(s => s.trim()).filter(Boolean)),
  ],
  grade: [...new Set(row.grades.map(s => s.trim()).filter(Boolean))],
  title: row.title.trim(),
  publishAt: publishAtStringForApi(row),
  keywords: row.tags.length ? row.tags : undefined,
  summary: row.summary.trim() || undefined,
  content: row.contentHtml || '<p></p>',
  coverImageUrl: coverImageUrl.trim() ? coverImageUrl.trim() : null,
  youtubeUrl: row.youtubeUrl.trim() ? row.youtubeUrl.trim() : null,
  attachments: attachments.length ? attachments : [],
  status: row.status,
});

/** 後端 class-validator 風格訊息中的欄位 key → 表單用語 */
const ARTICLE_BACKEND_FIELD_LABELS: Record<string, string> = {
  subject: '科目',
  academicYear: '學年度',
  grade: '年級（冊次）',
  eduCode: '學程',
  title: '標題',
  content: '內文',
  publishAt: '上架日期',
};

/**
 * 將後端驗證錯誤轉成「請選擇〇〇」；無法辨識時回傳原文。
 */
export const formatArticleBackendValidationMessage = (raw: string): string => {
  const cleaned = raw.replace(/\*/g, '');
  const re = /property\s+(\w+)\s+has failed/gi;
  const seen = new Set<string>();
  const keys: string[] = [];
  let m: RegExpExecArray | null;
  const r = new RegExp(re.source, re.flags);
  while ((m = r.exec(cleaned)) !== null) {
    const key = m[1];
    if (!seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  const parts = keys
    .map(k => ARTICLE_BACKEND_FIELD_LABELS[k])
    .filter((label): label is string => Boolean(label));
  if (parts.length === 0) return raw;
  return parts.map(p => `請選擇${p}`).join('、');
};
